import {
   Briefcase01Icon,
   ChartIcon,
   Edit01Icon,
   Home04Icon,
   Logout03Icon,
   Notebook01Icon,
   PencilEdit01Icon,
   Settings02Icon,
   UnfoldMoreIcon,
   UserMultipleIcon,
} from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { Link, useParams, useSearch } from "@tanstack/react-router"
import { Button } from "@unfiddle/ui/components/button"
import { Card } from "@unfiddle/ui/components/card"
import { DrawerTrigger } from "@unfiddle/ui/components/drawer"
import {
   Menu,
   MenuItem,
   MenuPopup,
   MenuTrigger,
} from "@unfiddle/ui/components/menu"
import { ScrollArea } from "@unfiddle/ui/components/scroll-area"
import { cn } from "@unfiddle/ui/utils"
import { useAtom } from "jotai"
import * as React from "react"
import { toast } from "sonner"
import { useAuth } from "@/auth/hooks"
import { notificationPermissionStatusAtom } from "@/notification/store"
import { sendNotification } from "@/notification/utils"
import { CreateOrder } from "@/order/create/create-order"
import { UserAvatar } from "@/user/components/user-avatar"
import { WorkspaceLogo } from "@/workspace/components/workspace-logo"
import { WorkspaceMenuPopup } from "@/workspace/components/workspace-menu"

export function Sidebar({
   children,
   className,
   ...props
}: React.ComponentProps<"aside">) {
   return (
      <aside
         className={cn(
            "z-10 h-svh w-(--sidebar-width) shrink-0 max-md:hidden",
            className,
         )}
         {...props}
      >
         <div className="fixed flex h-full w-(--sidebar-width) flex-col">
            {children}
         </div>
      </aside>
   )
}

export function SidebarContent() {
   const auth = useAuth()
   const params = useParams({ from: "/_authed/$workspaceId/_layout" })
   const search = useSearch({ strict: false })

   return (
      <Sidebar>
         <div className="px-4 pt-4">
            <div className="flex items-center justify-between gap-3">
               <Menu>
                  <MenuTrigger
                     render={
                        <Button
                           className={cn(
                              "justify-start gap-1.5!",
                              auth.workspace.image ? "md:pl-0.5" : "md:pl-1",
                           )}
                           variant={"ghost"}
                           size={"lg"}
                        >
                           <WorkspaceLogo
                              size={auth.workspace.image ? 30 : 24}
                              workspace={auth.workspace}
                           />
                           <span className="line-clamp-1 py-px">
                              {auth.workspace.name}
                           </span>
                           <HugeiconsIcon
                              icon={UnfoldMoreIcon}
                              className="-mr-2 ml-auto size-4 shrink-0 text-muted"
                           />
                        </Button>
                     }
                  />
                  <WorkspaceMenuPopup />
               </Menu>
               <Button
                  kind={"icon"}
                  variant={"ghost"}
                  nativeButton={false}
                  render={
                     <Link
                        to={"/$workspaceId/settings"}
                        params={params}
                        search={search}
                        className={
                           "text-muted hover:text-foreground/90 aria-[current=page]:text-foreground/90"
                        }
                     />
                  }
               >
                  <HugeiconsIcon
                     icon={Settings02Icon}
                     className="size-4.5"
                  />
               </Button>
            </div>
            <CreateOrder>
               <DrawerTrigger
                  render={
                     <Button
                        variant={"secondary"}
                        className="mt-3 w-full"
                        size={"lg"}
                     >
                        <HugeiconsIcon icon={PencilEdit01Icon} />
                        Нове замовлення
                     </Button>
                  }
               />
            </CreateOrder>
         </div>
         <ScrollArea render={<nav className="p-4" />}>
            <ul className="space-y-1">
               <li>
                  <Link
                     to={"/$workspaceId"}
                     params={params}
                     search={search}
                     activeOptions={{ exact: true, includeSearch: false }}
                     className={
                        "group flex h-8 items-center justify-start gap-2 rounded-md px-2 text-base text-foreground/80 leading-none transition-all duration-75 hover:bg-surface-3 hover:text-foreground aria-[current=page]:bg-surface-4/75 aria-[current=page]:text-foreground"
                     }
                  >
                     <HugeiconsIcon
                        icon={Home04Icon}
                        className="size-4.5"
                     />
                     <span className="pt-px"> Головна</span>
                  </Link>
               </li>
               <li>
                  <Link
                     to={"/$workspaceId/estimates"}
                     params={params}
                     search={search}
                     className={
                        "group flex h-8 items-center justify-start gap-2 rounded-md px-2 text-base text-foreground/80 leading-none transition-all duration-75 hover:bg-surface-3 hover:text-foreground aria-[current=page]:bg-surface-4/75 aria-[current=page]:text-foreground"
                     }
                  >
                     <HugeiconsIcon
                        icon={Notebook01Icon}
                        className="size-4.5"
                     />
                     <span className="pt-px"> Прорахунок</span>
                  </Link>
               </li>
               <li>
                  <Link
                     to={"/$workspaceId/clients"}
                     params={params}
                     search={search}
                     className={
                        "group flex h-8 items-center justify-start gap-2 rounded-md px-2 text-base text-foreground/80 leading-none transition-all duration-75 hover:bg-surface-3 hover:text-foreground aria-[current=page]:bg-surface-4/75 aria-[current=page]:text-foreground"
                     }
                  >
                     <HugeiconsIcon
                        icon={Briefcase01Icon}
                        className="size-4.5"
                     />
                     <span className="pt-px"> Клієнти</span>
                  </Link>
               </li>
               <li>
                  <Link
                     to={"/$workspaceId/board"}
                     params={params}
                     search={search}
                     className={
                        "group flex h-8 items-center justify-start gap-2 rounded-md px-2 text-base text-foreground/80 leading-none transition-all duration-75 hover:bg-surface-3 hover:text-foreground aria-[current=page]:bg-surface-4/75 aria-[current=page]:text-foreground"
                     }
                  >
                     <HugeiconsIcon
                        icon={Edit01Icon}
                        className="size-4.5"
                     />
                     <span className="pt-px"> Поставки</span>
                  </Link>
               </li>
               <li>
                  <Link
                     to={"/$workspaceId/analytics"}
                     params={params}
                     search={search}
                     className={
                        "group flex h-8 items-center justify-start gap-2 rounded-md px-2 text-base text-foreground/80 leading-none transition-all duration-75 hover:bg-surface-3 hover:text-foreground aria-[current=page]:bg-surface-4/75 aria-[current=page]:text-foreground"
                     }
                  >
                     <HugeiconsIcon
                        icon={ChartIcon}
                        className="size-4.5"
                     />
                     <span className="pt-px"> Аналітика</span>
                  </Link>
               </li>
               <li>
                  <Link
                     to={"/$workspaceId/team"}
                     params={params}
                     search={search}
                     className={
                        "group flex h-8 items-center justify-start gap-2 rounded-md px-2 text-base text-foreground/80 leading-none transition-all duration-75 hover:bg-surface-3 hover:text-foreground aria-[current=page]:bg-surface-4/75 aria-[current=page]:text-foreground"
                     }
                  >
                     <HugeiconsIcon
                        icon={UserMultipleIcon}
                        className="size-4.5"
                     />
                     <span className="pt-px"> Команда</span>
                  </Link>
               </li>
            </ul>
         </ScrollArea>
         <div className="mt-auto p-4 pt-1">
            <NotificationPermissionCard />
            <Menu>
               <MenuTrigger
                  render={
                     <Button
                        className="w-full justify-start gap-2! [--active-color:var(--color-surface-3)] md:pl-1.5"
                        variant={"ghost"}
                     >
                        <UserAvatar user={auth.user} />
                        <span className="line-clamp-1 py-px text-sm">
                           {auth.user.email}
                        </span>
                     </Button>
                  }
               />
               <MenuPopup className={"min-w-(--anchor-width)!"}>
                  <MenuItem
                     destructive
                     onClick={() => auth.signout.mutate()}
                  >
                     <HugeiconsIcon icon={Logout03Icon} /> Вийти з аккаунту
                  </MenuItem>
               </MenuPopup>
            </Menu>
         </div>
      </Sidebar>
   )
}

function NotificationPermissionCard() {
   const [permissionStatus, setPermissionStatus] = useAtom(
      notificationPermissionStatusAtom,
   )

   React.useEffect(() => {
      if (permissionStatus === "denied") return

      if ("Notification" in window) {
         setPermissionStatus(Notification.permission)
      }
   }, [])

   if (permissionStatus !== "default") return null

   return (
      <Card className="motion-preset-blur-up-sm mb-3">
         <p className="-mt-1 text-muted text-sm">
            Надсилати сповіщення про нові повідомлення?
         </p>
         <Button
            size="sm"
            className="mt-3 w-full"
            onClick={async () => {
               try {
                  let permission: NotificationPermission

                  if ("Notification" in window) {
                     permission = await Notification.requestPermission()
                     if (permission === "granted") {
                        sendNotification({
                           title: "Сповіщення увімкнено",
                           body: "Тепер ви будете отримувати сповіщення про нові повідомлення у чаті.",
                        })
                     }
                     return setPermissionStatus(permission)
                  }

                  return toast.error("Ваш браузер не підтримує сповіщення")
               } catch (error) {
                  console.error(
                     "Error requesting notification permission:",
                     error,
                  )
               }
            }}
         >
            Так, дозволити
         </Button>
         <Button
            size="sm"
            variant={"secondary"}
            className="mt-1.5 w-full"
            onClick={() => {
               return setPermissionStatus("denied")
            }}
         >
            Ні, дякую
         </Button>
      </Card>
   )
}
